import React from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { isEmpty } from 'ramda'
import { Box, IconEyeSlash } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'

import { Broker, Listing } from '@/types'
import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { Button } from '@/components/button-new'
import ListingSection from '@/components/listing-section'

const { DASHBOARD_URL } = ENV_VARS

type Appointment = {
  id: string
  duration: string
  hidden: string
  last_snooze_time: string | null
  calendar_event_id: string | null
  listing_id: string
  max_attendees: string
  meeting_at: string | null
  user_count: number
}
type Props = {
  landlord: {
    id: string
    picture: string
    company: {
      name: string
      address: string
      state: string
      city: string
    }
    broker: Broker
  }
  isListingLandlord: boolean
  listings: Listing

  appointments: Appointment[]
  onAppointmentClick: () => void
}

const StyledListingSection = styled(ListingSection)`
  @media screen and (max-width: ${breakpoints.desktop}) {
    margin-bottom: 32px;
  }
`
const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  background: ${colors.background};
  border-radius: 8px;
  padding: 0 24px;

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 24px 0;

    :not(:last-child) {
      border-bottom: 1px solid #eee;
    }

    @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
      align-items: flex-start;
    }
  }
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
`
const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`
const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: 8px;
`
const Text = styled.p`
  font-family: 'Inter';
  font-weight: 500;
  font-size: ${fontSizes.regular};
  line-height: 24px;
  margin: 0;
  width: 100%;
`
const SmallText = styled.p`
  font-family: 'Inter';
  font-weight: 500;
  font-size: ${fontSizes.small};
  line-height: 16px;
  color: ${vars.foregroundColor.caution};
  margin: 0 0 0 12px;
  width: 100%;
`

const StyledButton = styled(Button)`
  width: 110px;
  height: 40px;
  justify-self: flex-end;
  font-weight: ${fontWeights.medium};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const ListingContact = ({
  listings,
  appointments = [],
  isListingLandlord,
  onAppointmentClick
}: Props) => {
  const { t } = useTranslation()

  const showAddAppointment = isListingLandlord && isEmpty(appointments)

  return (
    <StyledListingSection
      title={t('listing:b.listing.view.listing_tour.text', 'Tour This Listing')}
      customGap="0px"
    >
      <InnerWrapper>
        <Wrapper>
          {showAddAppointment ? (
            <Column>
              <Text>
                {t(
                  'listing:b.listing.view.scheduled_tour.text',
                  'Schedule showing times for renters to tour this listing'
                )}
              </Text>
              <Row>
                <Box marginRight="xxxsmall">
                  <IconEyeSlash tone="caution" size="small" />
                </Box>
                <SmallText>
                  {t(
                    'listing:b.listing.view.visible_to_team.text',
                    'This is only visible to you and your team until you add a showing.'
                  )}
                </SmallText>
              </Row>
            </Column>
          ) : (
            <Text>
              {t(
                'listing:b.listing.view.listing_tour_renter.text',
                'Limited spots available. Reserve now to secure an appointment.'
              )}
            </Text>
          )}
          {showAddAppointment ? (
            <StyledButton
              theme="secondary"
              href={`${DASHBOARD_URL}/listing/${listings.id}/showings`}
              target="_blank"
            >
              {t('listing:b.listing.view.add_showings.text', 'Add showings')}
            </StyledButton>
          ) : (
            <StyledButton theme="secondary" onClick={onAppointmentClick}>
              {t(
                'listing:b.listing.view.book_a_showing.text',
                'Book a showing'
              )}
            </StyledButton>
          )}
        </Wrapper>
      </InnerWrapper>
    </StyledListingSection>
  )
}

export default ListingContact
